
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import * as E from 'app/calcs/expenses'
import * as utils from 'app/utils'
import * as _ from 'lodash'
import * as moment from 'moment'

const debug = utils.makeDebug(false)
const maxScore = 150
const upperBreak = 95
const lowerBreak = 80

interface IScoreTransactionsOutput {
  when: moment.Moment
  score: number
}

/**
 * Calculate a retirement health score from income and expenses. Taken from the
 * Fidelity Investments retirement score described here:
 *
 * If >= 95% of total expenses are covered by income, return that percentage
 * If >= 80% of fixed expenses are covered by income, return that percentage projected into a range of 80-95
 * Otherwise, return the percentage of fixed expenses covered by income projected into a range of 0-80
 *
 * @param income
 * @param fixedExpenses
 * @param descretionaryExpenses
 *
 * @returns the score, from 0 to maxScore
 */
function calculateRetirementScore(income: number, fixedExpenses: number, discretionaryExpenses: number): number {
  const totalExpenses = fixedExpenses + discretionaryExpenses
  if (0 === totalExpenses) { return maxScore }
  const applicableIncome = income //Math.min(income, totalExpenses)
  const p1 = 100 * applicableIncome / totalExpenses

  // if upperBreak% of all expenses are covered by income, that's the score
  if (p1 >= upperBreak) {
    //debug('income: ' + income + ', fixed: ' + fixedExpenses + ', desc: ' + descretionaryExpenses + ' -- covers ' + upperBreak + ' => ' + p1)
    return (p1 > maxScore) ? maxScore : p1
  }

  if (applicableIncome > fixedExpenses) {
    // if all the fixed expenses are covered, compute a score between lowerBreak% & (almost) upperBreak%
    const coveredDescretionaryExpensePercentage = (applicableIncome - fixedExpenses) / discretionaryExpenses
    const spread = (upperBreak - lowerBreak) * coveredDescretionaryExpensePercentage
    //debug('income: ' + income + ', fixed: ' + fixedExpenses + ', desc: ' + descretionaryExpenses + ' applicable > fixed => spread ' + spread)
    return (lowerBreak + spread)
  }

  // Oh dear, this uses is in trouble and can't cover enough fixed espenses
  const appliedToFixed = Math.min(fixedExpenses, applicableIncome)
  const p2 = appliedToFixed / fixedExpenses * lowerBreak
  //debug('income: ' + income + ', fixed: ' + fixedExpenses + ', desc: ' + descretionaryExpenses + ' appliedToFixed(' + appliedToFixed + ') > fixed: => p2 ' + p2)
  return p2
}

function getScorePhrase(score) {
  if (score >= upperBreak) { return 'on target' }
  if (score >= lowerBreak) { return 'good' }
  if (score >= 65) { return 'fair' }
  return 'insufficient';
}

function scoreTransactions(items: TX.ITransactionItem[]): IScoreTransactionsOutput[] {

  debug('Scoring transactions');

  const groups = _.groupBy(items, (q) => q.when.format('MM-YYYY'))
  //debug('groups: ' + JSON.stringify(groups, undefined, 4))

  const scores = _(groups).keysIn()
    .map((key) => {
      const monthWhen = moment(key, 'MM-YYYY')
      //debug('Starting month: ' + key)
      const monthData = groups[key]

      // bin each item in preparation for scoring the month
      const bins = _.reduce(monthData, (r, item) => {
        _(item.what).keysIn().forEach((kind: E.AllExpenses) => {
          const a = item.what[kind]
          if (kind === 'fixed') {
            r.fixed += a
          } else if (kind === 'discretionary') {
            r.discretionary += a
          } else {
            r.income += a
          }
        })
        return r
      }, { income: 0, fixed: 0, discretionary: 0 })

      const score = calculateRetirementScore(bins.income, -bins.fixed, -bins.discretionary)
      //debug('score for ' + key + ': ' + score + ' from: income: ' + bins.income + ', fixed: ' + bins.fixed + ', disc: ' + bins.discretionary)
      return { when: monthWhen, score }
    })
    .value()
  return scores
}

export {
  IScoreTransactionsOutput,

  calculateRetirementScore,
  getScorePhrase,
  scoreTransactions
}



// WEBPACK FOOTER //
// ./src/app/calcs/Planners/Fidelity/index.ts